/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { DatacenterConnection } from "../DatacenterConnectionsData";
import { 
  getHardwareByDatacenterId, 
  getPortsByHardwareId,
  getHardwareById,
  getPortId,
  Hardware 
} from "../../Hardware/request/request";
import { getHardwareListByDataCenter } from "../../Kontroledge/Port/request/request";
import { getProviders, getAllDatacenters, getDatacenterById } from "../../DataCenter/request/request";

export interface DatacenterConnectionsEditModalProps {
  isOpen: boolean;
  isLoading: boolean;
  connection: DatacenterConnection | null;
  onClose: () => void;
  onSave: (updatedConnection: Partial<DatacenterConnection>) => Promise<void>;
}

export const DatacenterConnectionsEditModal: React.FC<DatacenterConnectionsEditModalProps> = ({
  isOpen,
  isLoading,
  connection,
  onClose,
  onSave,
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  
  // Form state
  const [formData, setFormData] = useState<Partial<DatacenterConnection>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Tab states
  const [activeSourceTab, setActiveSourceTab] = useState<"datacenter" | "hardware" | "port">("datacenter");
  const [activeDestTab, setActiveDestTab] = useState<"datacenter" | "hardware" | "port">("datacenter");
  
  // State for adding destination data
  const [isAddingDestination, setIsAddingDestination] = useState(false);

  // Source dropdowns data
  const [sourceDataCenters, setSourceDataCenters] = useState<any[]>([]);
  const [isLoadingSourceDataCenters, setIsLoadingSourceDataCenters] = useState(false);
  const [isLoadingSourceDatacenterDetails, setIsLoadingSourceDatacenterDetails] = useState(false);
  const [selectedSourceDatacenter, setSelectedSourceDatacenter] = useState<any | null>(null);
  const [selectedSourceDatacenterLabel, setSelectedSourceDatacenterLabel] = useState<string>("");
  
  // Source hardware data
  const [sourceHardwareList, setSourceHardwareList] = useState<any[]>([]);
  const [isLoadingSourceHardwareList, setIsLoadingSourceHardwareList] = useState(false);
  const [isLoadingSourceHardwareDetails, setIsLoadingSourceHardwareDetails] = useState(false);
  const [selectedSourceHardwareDetails, setSelectedSourceHardwareDetails] = useState<any | null>(null);
  const [selectedSourceHardwareLabel, setSelectedSourceHardwareLabel] = useState<string>("");
  
  // Source port data
  const [sourcePortsList, setSourcePortsList] = useState<any[]>([]);
  const [isLoadingSourcePortsList, setIsLoadingSourcePortsList] = useState(false);
  const [isLoadingSourcePortDetails, setIsLoadingSourcePortDetails] = useState(false);
  const [selectedSourcePortDetails, setSelectedSourcePortDetails] = useState<any | null>(null);
  const [selectedSourcePortLabel, setSelectedSourcePortLabel] = useState<string>("");

  // Destination dropdowns data
  const [dataCenters, setDataCenters] = useState<any[]>([]);
  const [destHardware, setDestHardware] = useState<Hardware[]>([]);
  const [destPorts, setDestPorts] = useState<any[]>([]);
  const [hardwareListForDest, setHardwareListForDest] = useState<any[]>([]);
  const [isLoadingDataCenters, setIsLoadingDataCenters] = useState(false);
  const [isLoadingDestHardware, setIsLoadingDestHardware] = useState(false);
  const [isLoadingDestPorts, setIsLoadingDestPorts] = useState(false);
  const [isLoadingHardwareList, setIsLoadingHardwareList] = useState(false);
  const [isLoadingHardwareDetails, setIsLoadingHardwareDetails] = useState(false);
  const [isLoadingDestDatacenterDetails, setIsLoadingDestDatacenterDetails] = useState(false);
  const [selectedDestDatacenter, setSelectedDestDatacenter] = useState<any | null>(null);
  const [selectedDestHardwareDetails, setSelectedDestHardwareDetails] = useState<any | null>(null);
  const [selectedDestPortDetails, setSelectedDestPortDetails] = useState<any | null>(null);

  // Destination selections (for dropdowns)
  const [selectedDestDatacenterLabel, setSelectedDestDatacenterLabel] = useState<string>("");
  const [selectedDestHardwareLabel, setSelectedDestHardwareLabel] = useState<string>("");
  const [selectedDestPortLabel, setSelectedDestPortLabel] = useState<string>("");

  // Providers data
  const [providers, setProviders] = useState<Array<{ id: number; name: string; idAddon: string }>>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [selectedProviderLabel, setSelectedProviderLabel] = useState<string>("");

  // Track last fetched IDs to avoid unnecessary reloads
  const [lastSourceDatacenterId, setLastSourceDatacenterId] = useState<string | null>(null);
  const [lastDestDatacenterId, setLastDestDatacenterId] = useState<string | null>(null);
  const [lastSourceHardwareId, setLastSourceHardwareId] = useState<number | null>(null);
  const [lastDestHardwareId, setLastDestHardwareId] = useState<number | null>(null);

  // Handle destination tab change
  const handleDestTabChange = (tab: "datacenter" | "hardware" | "port") => {
    setActiveDestTab(tab);
    // Reset adding state when switching tabs - each tab manages its own "Add +" state
    setIsAddingDestination(false);
  };

  // Initialize form data when connection changes
  useEffect(() => {
    if (connection) {
      setFormData({
        subId: connection.subId || "",
        datacenterIdSource: connection.datacenterIdSource || "",
        hardwareIdSource: connection.hardwareIdSource ?? null,
        portIdSource: connection.portIdSource ?? null,
        capacityMbps: connection.capacityMbps || 0,
        connectionType: connection.connectionType || "",
        datacenterIdDest: connection.datacenterIdDest,
        hardwareIdDest: connection.hardwareIdDest ?? null,
        portIdDest: connection.portIdDest ?? null,
        // Include source and dest objects for editing
        datacenterSource: connection.datacenterSource,
        hardwareSource: connection.hardwareSource,
        portSource: connection.portSource,
        datacenterDest: connection.datacenterDest,
        hardwareDest: connection.hardwareDest,
        portDest: connection.portDest,
      });
      setErrorMessage(null);
      setIsAddingDestination(false);
    }
  }, [connection]);

  // Initialize source dropdown selections when sourceDataCenters are loaded and connection has source data
  useEffect(() => {
    if (connection && sourceDataCenters.length > 0) {
      if (connection.datacenterSource && !selectedSourceDatacenterLabel) {
        const dc = sourceDataCenters.find(dc => dc.datacenterId === connection.datacenterSource?.datacenterId);
        if (dc) {
          setSelectedSourceDatacenterLabel(dc.displayName);
          // Set the datacenter details from connection
          setSelectedSourceDatacenter(connection.datacenterSource);
        }
      }
      if (connection.hardwareSource && !selectedSourceHardwareLabel) {
        const hardwareLabel = connection.hardwareSource.hostname && connection.hardwareSource.serialNumber
          ? `${connection.hardwareSource.hostname} - ${connection.hardwareSource.serialNumber}`
          : connection.hardwareSource.hostname || "";
        setSelectedSourceHardwareLabel(hardwareLabel);
        // Set hardware details if available
        if (connection.hardwareSource.id) {
          setSelectedSourceHardwareDetails(connection.hardwareSource);
        }
      }
      if (connection.portSource && !selectedSourcePortLabel) {
        setSelectedSourcePortLabel(connection.portSource.portNumber || "");
        // Set port details if available
        if (connection.portSource.id) {
          setSelectedSourcePortDetails(connection.portSource);
        }
      }
    }
  }, [connection, sourceDataCenters, selectedSourceDatacenterLabel, selectedSourceHardwareLabel, selectedSourcePortLabel]);

  // Load hardware options when datacenterIdSource is available (from initial response)
  useEffect(() => {
    if (!isOpen || !isAuthenticated || !connection?.datacenterIdSource) return;
    
    // Only load if we don't already have hardware list loaded
    if (sourceHardwareList.length > 0) return;
    
    // Load hardware options using datacenterIdSource from response
    (async () => {
      try {
        setIsLoadingSourceHardwareList(true);
        const token = await getAccessTokenSilently();
        const resp = await getHardwareListByDataCenter(token, connection.datacenterIdSource);
        const hwRaw = extractApiData(resp) ?? [];
        setSourceHardwareList(hwRaw);
      } catch (error) {
        console.error("Error fetching source hardware list on init:", error);
        setSourceHardwareList([]);
      } finally {
        setIsLoadingSourceHardwareList(false);
      }
    })();
  }, [connection?.datacenterIdSource, isOpen, isAuthenticated, getAccessTokenSilently, sourceHardwareList.length]);

  // Load port options when hardwareIdSource is available (from initial response)
  useEffect(() => {
    if (!isOpen || !isAuthenticated || !connection?.hardwareIdSource) return;
    
    // Only load if we don't already have ports list loaded
    if (sourcePortsList.length > 0) return;
    
    // Load port options using hardwareIdSource from response
    (async () => {
      try {
        setIsLoadingSourcePortsList(true);
        const token = await getAccessTokenSilently();
        const resp = await getPortsByHardwareId(token, String(connection.hardwareIdSource));
        const portData = extractApiData(resp);
        const portsArray = portData?.ports || [];
        setSourcePortsList(portsArray);
      } catch (error) {
        console.error("Error fetching source ports list on init:", error);
        setSourcePortsList([]);
      } finally {
        setIsLoadingSourcePortsList(false);
      }
    })();
  }, [connection?.hardwareIdSource, isOpen, isAuthenticated, getAccessTokenSilently, sourcePortsList.length]);

  // Initialize destination dropdown selections when dataCenters and providers are loaded and connection has dest data
  useEffect(() => {
    if (connection && dataCenters.length > 0) {
      if (connection.datacenterDest && !selectedDestDatacenterLabel) {
        const dc = dataCenters.find(dc => dc.datacenterId === connection.datacenterDest?.datacenterId);
        if (dc) {
          setSelectedDestDatacenterLabel(dc.displayName);
          // Set the datacenter details from connection
          setSelectedDestDatacenter(connection.datacenterDest);
        }
      }
      if (connection.hardwareDest && !selectedDestHardwareLabel) {
        const hardwareLabel = connection.hardwareDest.hostname && connection.hardwareDest.serialNumber
          ? `${connection.hardwareDest.hostname} - ${connection.hardwareDest.serialNumber}`
          : connection.hardwareDest.hostname || "";
        setSelectedDestHardwareLabel(hardwareLabel);
        // Set hardware details if available
        if (connection.hardwareDest.id) {
          setSelectedDestHardwareDetails(connection.hardwareDest);
        }
      }
      if (connection.portDest && !selectedDestPortLabel) {
        setSelectedDestPortLabel(connection.portDest.portNumber || "");
        // Set port details if available
        if (connection.portDest.id) {
          setFormData((prev) => ({
            ...prev,
            portDest: connection.portDest,
          }));
        }
      }
      if (connection && providers.length > 0 && connection.datacenterDest) {
        const providerId = connection.datacenterDest.providerId;
        if (providerId && !selectedProviderLabel) {
          const provider = providers.find(p => p.id === providerId);
          if (provider) {
            setSelectedProviderLabel(provider.name);
          }
        }
      }
    }
  }, [connection, dataCenters, providers, selectedDestDatacenterLabel, selectedDestHardwareLabel, selectedDestPortLabel, selectedProviderLabel]);

  // Reset adding state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsAddingDestination(false);
      setSelectedDestDatacenterLabel("");
      setSelectedDestHardwareLabel("");
      setSelectedDestPortLabel("");
      setSelectedProviderLabel("");
      setSelectedDestDatacenter(null);
      setSelectedDestHardwareDetails(null);
      setSelectedSourceDatacenter(null);
      setSelectedSourceDatacenterLabel("");
      setSelectedSourceHardwareDetails(null);
      setSelectedSourceHardwareLabel("");
      setSelectedSourcePortDetails(null);
      setSelectedSourcePortLabel("");
      setSourceHardwareList([]);
      setSourcePortsList([]);
      setHardwareListForDest([]);
      setDestHardware([]);
      setDestPorts([]);
      // Reset tracking IDs
      setLastSourceDatacenterId(null);
      setLastDestDatacenterId(null);
      setLastSourceHardwareId(null);
      setLastDestHardwareId(null);
    }
  }, [isOpen]);

  // Fetch datacenters and providers when modal opens
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      // Fetch datacenters for Destination
      (async () => {
        try {
          setIsLoadingDataCenters(true);
          const token = await getAccessTokenSilently();
          const resp = await getAllDatacenters(token);
          const dcRaw = extractApiData(resp) ?? [];
          setDataCenters(dcRaw);
        } catch (error) {
          console.error("Error fetching datacenters:", error);
          setDataCenters([]);
        } finally {
          setIsLoadingDataCenters(false);
        }
      })();

      // Fetch datacenters for Source
      (async () => {
        try {
          setIsLoadingSourceDataCenters(true);
          const token = await getAccessTokenSilently();
          const resp = await getAllDatacenters(token);
          const dcRaw = extractApiData(resp) ?? [];
          setSourceDataCenters(dcRaw);
        } catch (error) {
          console.error("Error fetching source datacenters:", error);
          setSourceDataCenters([]);
        } finally {
          setIsLoadingSourceDataCenters(false);
        }
      })();

      (async () => {
        try {
          setIsLoadingProviders(true);
          const token = await getAccessTokenSilently();
          const resp = await getProviders(token);
          const providersData = extractApiData(resp) ?? [];
          setProviders(providersData);
        } catch (error) {
          console.error("Error fetching providers:", error);
          setProviders([]);
        } finally {
          setIsLoadingProviders(false);
        }
      })();
    }
  }, [isOpen, isAuthenticated, getAccessTokenSilently]);

  // Fetch hardware list for Source when in hardware or port tab and datacenter is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || (activeSourceTab !== "hardware" && activeSourceTab !== "port")) {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    // Get datacenter ID from selectedSourceDatacenter, formData.datacenterSource, or formData.datacenterIdSource
    const datacenterId = selectedSourceDatacenter?.datacenterId || formData.datacenterSource?.datacenterId || formData.datacenterIdSource;
    
    if (!datacenterId) {
      // Only clear if datacenter is not selected
      if (!selectedSourceDatacenter && !formData.datacenterSource && !formData.datacenterIdSource) {
        setSourceHardwareList([]);
        setLastSourceDatacenterId(null);
      }
      return;
    }

    // Only fetch if datacenter changed (not if already fetched for this ID)
    if (lastSourceDatacenterId === datacenterId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingSourceHardwareList(true);
        const token = await getAccessTokenSilently();
        const resp = await getHardwareListByDataCenter(token, datacenterId);
        const hwRaw = extractApiData(resp) ?? [];
        setSourceHardwareList(hwRaw);
        setLastSourceDatacenterId(datacenterId);
      } catch (error) {
        console.error("Error fetching source hardware list:", error);
        setSourceHardwareList([]);
        setLastSourceDatacenterId(null);
      } finally {
        setIsLoadingSourceHardwareList(false);
      }
    })();
  }, [activeSourceTab, selectedSourceDatacenter, formData.datacenterSource, formData.datacenterIdSource, isOpen, isAuthenticated, getAccessTokenSilently, lastSourceDatacenterId]);

  // Fetch hardware list for Destination when in hardware or port tab and datacenter is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || (activeDestTab !== "hardware" && activeDestTab !== "port")) {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    // Get datacenter ID from selectedDestDatacenter, formData.datacenterDest, or formData.datacenterIdDest
    const datacenterId = selectedDestDatacenter?.datacenterId || formData.datacenterDest?.datacenterId || formData.datacenterIdDest;
    
    if (!datacenterId) {
      // Only clear if datacenter is not selected
      if (!selectedDestDatacenter && !formData.datacenterDest && !formData.datacenterIdDest) {
        setHardwareListForDest([]);
        setLastDestDatacenterId(null);
      }
      return;
    }

    // Only fetch if datacenter changed (not if already fetched for this ID)
    if (lastDestDatacenterId === datacenterId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingHardwareList(true);
        const token = await getAccessTokenSilently();
        const resp = await getHardwareListByDataCenter(token, datacenterId);
        const hwRaw = extractApiData(resp) ?? [];
        setHardwareListForDest(hwRaw);
        setLastDestDatacenterId(datacenterId);
      } catch (error) {
        console.error("Error fetching destination hardware list:", error);
        setHardwareListForDest([]);
        setLastDestDatacenterId(null);
      } finally {
        setIsLoadingHardwareList(false);
      }
    })();
  }, [activeDestTab, selectedDestDatacenter, formData.datacenterDest, formData.datacenterIdDest, isOpen, isAuthenticated, getAccessTokenSilently, lastDestDatacenterId]);

  // Fetch ports list for Source when in port tab and hardware is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || activeSourceTab !== "port") {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    // Get hardware ID from selectedSourceHardwareDetails, formData.hardwareSource, or formData.hardwareIdSource
    const hardwareId = selectedSourceHardwareDetails?.id || formData.hardwareSource?.id || formData.hardwareIdSource;
    
    if (!hardwareId) {
      // Only clear if hardware is not selected
      if (!selectedSourceHardwareDetails && !formData.hardwareSource && !formData.hardwareIdSource) {
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
      }
      return;
    }

    // Only fetch if hardware changed (not if already fetched for this ID)
    if (lastSourceHardwareId === hardwareId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingSourcePortsList(true);
        const token = await getAccessTokenSilently();
        const resp = await getPortsByHardwareId(token, String(hardwareId));
        const portData = extractApiData(resp);
        const portsArray = portData?.ports || [];
        setSourcePortsList(portsArray);
        setLastSourceHardwareId(hardwareId);
      } catch (error) {
        console.error("Error fetching source ports list:", error);
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
      } finally {
        setIsLoadingSourcePortsList(false);
      }
    })();
  }, [activeSourceTab, selectedSourceHardwareDetails, formData.hardwareSource, formData.hardwareIdSource, isOpen, isAuthenticated, getAccessTokenSilently, lastSourceHardwareId]);

  // Fetch ports list for Destination when in port tab and hardware is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || activeDestTab !== "port") {
      // Don't reset data when switching tabs - keep it for persistence
      return;
    }

    // Get hardware ID from selectedDestHardwareDetails, formData.hardwareDest, or formData.hardwareIdDest
    const hardwareId = selectedDestHardwareDetails?.id || formData.hardwareDest?.id || formData.hardwareIdDest;
    
    if (!hardwareId) {
      // Only clear if hardware is not selected
      if (!selectedDestHardwareDetails && !formData.hardwareDest && !formData.hardwareIdDest) {
        setDestPorts([]);
        setLastDestHardwareId(null);
      }
      return;
    }

    // Only fetch if hardware changed (not if already fetched for this ID)
    if (lastDestHardwareId === hardwareId) {
      return;
    }

    (async () => {
      try {
        setIsLoadingDestPorts(true);
        const token = await getAccessTokenSilently();
        const resp = await getPortsByHardwareId(token, String(hardwareId));
        const portData = extractApiData(resp);
        const portsArray = portData?.ports || [];
        setDestPorts(portsArray);
        setLastDestHardwareId(hardwareId);
      } catch (error) {
        console.error("Error fetching destination ports list:", error);
        setDestPorts([]);
        setLastDestHardwareId(null);
      } finally {
        setIsLoadingDestPorts(false);
      }
    })();
  }, [activeDestTab, selectedDestHardwareDetails, formData.hardwareDest, formData.hardwareIdDest, isOpen, isAuthenticated, getAccessTokenSilently, lastDestHardwareId]);

  // Fetch ports when destination hardware is selected
  useEffect(() => {
    if (!isOpen || !isAuthenticated || !selectedDestHardwareLabel) {
      setDestPorts([]);
      setSelectedDestPortLabel("");
      return;
    }

    (async () => {
      try {
        setIsLoadingDestPorts(true);
        const token = await getAccessTokenSilently();
        // Find hardware ID from serial number
        const hardware = destHardware.find(hw => hw.serialNumber === selectedDestHardwareLabel);
        if (hardware) {
          const resp = await getPortsByHardwareId(token, String(hardware.id));
          const portData = extractApiData(resp);
          const portsArray = portData?.ports || [];
          setDestPorts(portsArray);
        }
      } catch (error) {
        console.error("Error fetching destination ports:", error);
        setDestPorts([]);
      } finally {
        setIsLoadingDestPorts(false);
      }
    })();
  }, [selectedDestHardwareLabel, destHardware, isOpen, isAuthenticated, getAccessTokenSilently]);

  // Helper function to extract API data
  const extractApiData = (resp: any): any => {
    return (resp as any)?.data?.data ?? (resp as any)?.data;
  };

  const handleInputChange = (field: keyof DatacenterConnection, value: string | number | null | undefined) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === undefined ? undefined : value,
    }));
  };

  // Handle nested object changes (for source/dest objects)
  const handleNestedChange = (
    objectKey: "datacenterSource" | "hardwareSource" | "portSource" | "datacenterDest" | "hardwareDest" | "portDest",
    field: string,
    value: string | number | boolean | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [objectKey]: {
        ...(prev[objectKey] as any || {}),
        [field]: value,
      },
    }));
  };

  // Handle source datacenter selection
  const handleSourceDatacenterSelect = async (displayName: string) => {
    if (!displayName) {
      setSelectedSourceDatacenterLabel("");
      setSelectedSourceDatacenter(null);
      setFormData((prev) => ({
        ...prev,
        datacenterSource: undefined,
        datacenterIdSource: "",
      }));
      // Reset hardware and port in ALL tabs when datacenter is cleared
      setSelectedSourceHardwareLabel("");
      setSelectedSourceHardwareDetails(null);
      setSelectedSourcePortLabel("");
      setSelectedSourcePortDetails(null);
      setSourceHardwareList([]);
      setSourcePortsList([]);
      handleInputChange("hardwareIdSource", null);
      handleInputChange("portIdSource", null);
      setFormData((prev) => ({
        ...prev,
        hardwareSource: undefined,
        portSource: undefined,
      }));
      return;
    }
    
    setSelectedSourceDatacenterLabel(displayName);
    const datacenter = sourceDataCenters.find(dc => dc.displayName === displayName);
    if (!datacenter) return;
    
    try {
      setIsLoadingSourceDatacenterDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getDatacenterById(token, datacenter.datacenterId);
      const datacenterDetails = extractApiData(resp);
      
      if (datacenterDetails) {
        setSelectedSourceDatacenter(datacenterDetails);
        handleInputChange("datacenterIdSource", datacenterDetails.datacenterId);
        // Populate formData with the full datacenter data
        setFormData((prev) => ({
          ...prev,
          datacenterSource: datacenterDetails,
        }));
        
        // Reset hardware and port in ALL tabs when datacenter changes
        setSelectedSourceHardwareLabel("");
        setSelectedSourceHardwareDetails(null);
        setSelectedSourcePortLabel("");
        setSelectedSourcePortDetails(null);
        setSourceHardwareList([]);
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
        handleInputChange("hardwareIdSource", null);
        handleInputChange("portIdSource", null);
        setFormData((prev) => ({
          ...prev,
          hardwareSource: undefined,
          portSource: undefined,
        }));
      }
    } catch (error) {
      console.error("Error fetching source datacenter details:", error);
    } finally {
      setIsLoadingSourceDatacenterDetails(false);
    }
  };

  // Handle destination datacenter selection
  const handleDestDatacenterSelect = async (displayName: string) => {
    if (displayName === "Select All" || !displayName) {
      setSelectedDestDatacenterLabel("");
      setSelectedDestDatacenter(null);
      setFormData((prev) => ({
        ...prev,
        datacenterDest: undefined,
        datacenterIdDest: null,
      }));
      handleInputChange("datacenterIdDest", null);
      return;
    }
    
    setSelectedDestDatacenterLabel(displayName);
    const datacenter = dataCenters.find(dc => dc.displayName === displayName);
    if (!datacenter) return;
    
    try {
      setIsLoadingDestDatacenterDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getDatacenterById(token, datacenter.datacenterId);
      const datacenterDetails = extractApiData(resp);
      
      if (datacenterDetails) {
        setSelectedDestDatacenter(datacenterDetails);
        handleInputChange("datacenterIdDest", datacenterDetails.datacenterId);
        // Populate formData with the full datacenter data
        setFormData((prev) => ({
          ...prev,
          datacenterDest: datacenterDetails,
        }));
        // Reset hardware and port selections in ALL tabs when datacenter changes
        setSelectedDestHardwareLabel("");
        setSelectedDestHardwareDetails(null);
        setSelectedDestPortLabel("");
        setSelectedDestPortDetails(null);
        setHardwareListForDest([]);
        setDestHardware([]);
        setDestPorts([]);
        setLastDestHardwareId(null);
        handleInputChange("hardwareIdDest", null);
        handleInputChange("portIdDest", null);
        setFormData((prev) => ({
          ...prev,
          hardwareDest: undefined,
          portDest: undefined,
        }));
      }
    } catch (error) {
      console.error("Error fetching destination datacenter details:", error);
    } finally {
      setIsLoadingDestDatacenterDetails(false);
    }
  };

  // Handle source hardware selection
  const handleSourceHardwareSelect = async (hardwareOption: string) => {
    setSelectedSourceHardwareLabel(hardwareOption);
    
    // Extract hardware ID from the option (format: "hostname - serialNumber")
    const parts = hardwareOption.split(" - ");
    const hostname = parts[0];
    const serialNumber = parts[1];
    const hardware = sourceHardwareList.find(hw => 
      hw.hostname === hostname && hw.serialNumber === serialNumber
    );
    
    if (!hardware) return;
    
    try {
      setIsLoadingSourceHardwareDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getHardwareById(token, String(hardware.id));
      const hardwareDetails = extractApiData(resp);
      
      if (hardwareDetails) {
        setSelectedSourceHardwareDetails(hardwareDetails);
        handleInputChange("hardwareIdSource", hardware.id);
        // Populate formData with the full hardware data
        setFormData((prev) => ({
          ...prev,
          hardwareSource: hardwareDetails,
        }));
        
        // Reset port in ALL tabs when hardware changes
        setSelectedSourcePortLabel("");
        setSelectedSourcePortDetails(null);
        setSourcePortsList([]);
        setLastSourceHardwareId(null);
        handleInputChange("portIdSource", null);
        setFormData((prev) => ({
          ...prev,
          portSource: undefined,
        }));
      }
    } catch (error) {
      console.error("Error fetching source hardware details:", error);
    } finally {
      setIsLoadingSourceHardwareDetails(false);
    }
  };

  // Handle source port selection
  const handleSourcePortSelect = async (portNumber: string) => {
    setSelectedSourcePortLabel(portNumber);
    const port = sourcePortsList.find(p => p.portNumber === portNumber);
    
    if (!port) return;
    
    try {
      setIsLoadingSourcePortDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getPortId(token, String(port.id));
      const portDetails = extractApiData(resp);
      
      if (portDetails) {
        setSelectedSourcePortDetails(portDetails);
        handleInputChange("portIdSource", port.id);
        // Populate formData with the full port data
        setFormData((prev) => ({
          ...prev,
          portSource: portDetails,
        }));
      }
    } catch (error) {
      console.error("Error fetching source port details:", error);
    } finally {
      setIsLoadingSourcePortDetails(false);
    }
  };

  // Handle destination port selection (for port tab)
  const handleDestPortSelectForTab = async (portNumber: string) => {
    setSelectedDestPortLabel(portNumber);
    const port = destPorts.find(p => p.portNumber === portNumber);
    
    if (!port) return;
    
    try {
      setIsLoadingDestPorts(true);
      const token = await getAccessTokenSilently();
      const resp = await getPortId(token, String(port.id));
      const portDetails = extractApiData(resp);
      
      if (portDetails) {
        handleInputChange("portIdDest", port.id);
        // Populate formData with the full port data
        setFormData((prev) => ({
          ...prev,
          portDest: portDetails,
        }));
      }
    } catch (error) {
      console.error("Error fetching destination port details:", error);
    } finally {
      setIsLoadingDestPorts(false);
    }
  };

  // Handle destination hardware selection (for hardware tab)
  const handleDestHardwareSelectForTab = async (hardwareOption: string) => {
    setSelectedDestHardwareLabel(hardwareOption);
    
    // Extract hardware ID from the option (format: "hostname - serialNumber")
    const parts = hardwareOption.split(" - ");
    const hostname = parts[0];
    const serialNumber = parts[1];
    const hardware = hardwareListForDest.find(hw => 
      hw.hostname === hostname && hw.serialNumber === serialNumber
    );
    
    if (!hardware) return;
    
    try {
      setIsLoadingHardwareDetails(true);
      const token = await getAccessTokenSilently();
      const resp = await getHardwareById(token, String(hardware.id));
      const hardwareDetails = extractApiData(resp);
      
      if (hardwareDetails) {
        setSelectedDestHardwareDetails(hardwareDetails);
        handleInputChange("hardwareIdDest", hardware.id);
        // Populate formData with the full hardware data
        setFormData((prev) => ({
          ...prev,
          hardwareDest: hardwareDetails,
        }));
        
        // Reset port in ALL tabs when hardware changes
        setSelectedDestPortLabel("");
        setSelectedDestPortDetails(null);
        setDestPorts([]);
        setLastDestHardwareId(null);
        handleInputChange("portIdDest", null);
        setFormData((prev) => ({
          ...prev,
          portDest: undefined,
        }));
      }
    } catch (error) {
      console.error("Error fetching destination hardware details:", error);
    } finally {
      setIsLoadingHardwareDetails(false);
    }
  };

  // Handle destination hardware selection (for port tab - legacy)
  const handleDestHardwareSelect = (serialNumber: string) => {
    setSelectedDestHardwareLabel(serialNumber);
    const hardware = destHardware.find(hw => hw.serialNumber === serialNumber);
    if (hardware) {
      handleInputChange("hardwareIdDest", hardware.id);
      // Reset port selection
      setSelectedDestPortLabel("");
      setDestPorts([]);
      handleInputChange("portIdDest", null);
    }
  };

  // Handle destination port selection
  const handleDestPortSelect = (portNumber: string) => {
    setSelectedDestPortLabel(portNumber);
    const port = destPorts.find(p => p.portNumber === portNumber);
    if (port) {
      handleInputChange("portIdDest", port.id);
    }
  };


  // Get dropdown options
  const sourceDataCenterOptions = sourceDataCenters.map(dc => dc.displayName).sort();
  const dataCenterOptions = ["Select All", ...dataCenters.map(dc => dc.displayName).sort()];
  const hardwareOptions = destHardware.map(hw => hw.serialNumber).filter(Boolean).sort();
  const sourceHardwareOptions = sourceHardwareList.map(hw => 
    `${hw.hostname} - ${hw.serialNumber}`
  ).filter(Boolean).sort();
  const hardwareOptionsForDest = hardwareListForDest.map(hw => 
    `${hw.hostname} - ${hw.serialNumber}`
  ).filter(Boolean).sort();
  const sourcePortOptions = sourcePortsList.map(p => p.portNumber).filter(Boolean).sort();
  const portOptions = destPorts.map(p => p.portNumber).filter(Boolean).sort();
  const providerOptions = providers.map(p => p.name).sort();

  const handleSave = async () => {
    if (!connection) return;
    try {
      setIsSaving(true);
      setErrorMessage(null);
      
      const payload: any = {
        subId: formData.subId || "",
        datacenterIdSource: formData.datacenterIdSource || "",
        hardwareIdSource: formData.hardwareIdSource,
        portIdSource: formData.portIdSource,
        capacityMbps: formData.capacityMbps || 0,
        connectionType: formData.connectionType || "",
      };

      // Add optional destination fields if provided
      if (formData.datacenterIdDest) {
        payload.datacenterIdDest = formData.datacenterIdDest;
      }
      if (formData.hardwareIdDest !== null) {
        payload.hardwareIdDest = formData.hardwareIdDest;
      }
      if (formData.portIdDest !== null) {
        payload.portIdDest = formData.portIdDest;
      }

      await onSave(payload);
    } catch (error: any) {
      console.error("Error saving connection:", error);
      const errorMsg = error?.message || "An error occurred while updating the connection";
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const hasSourceData = connection && (
    connection.datacenterSource || 
    connection.hardwareSource || 
    connection.portSource
  );

  const hasDestData = connection && (
    connection.datacenterDest || 
    connection.hardwareDest || 
    connection.portDest
  );

  const SOURCE_TABS = [
    { id: "datacenter", label: "Datacenter" },
    { id: "hardware", label: "Hardware" },
    { id: "port", label: "Port" },
  ];

  const DEST_TABS = [
    { id: "datacenter", label: "Datacenter" },
    { id: "hardware", label: "Hardware" },
    { id: "port", label: "Port" },
  ];

  return (
    <>
      {/* Error Alert Modal */}
      <GenericAlert
        isOpen={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        onConfirm={() => setErrorMessage(null)}
        title="Error Updating Connection"
        description={errorMessage || ""}
        variant="error"
        confirmLabel="OK"
        hideCancel
      />

      <GenericModal 
        isOpen={isOpen} 
        onClose={onClose} 
        title="Edit Datacenter Connection"
        customWidth="max-w-6xl"
      >
        {isLoading ? (
          <div className="space-y-6">
            <div>
              <SkeletonBlock h={20} w="30%" className="mb-3" />
              <div className="grid grid-cols-3 gap-4">
                {Array.from({ length: 9 }).map((_, idx) => (
                  <SkeletonText key={idx} lines={2} lineHeight={14} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          connection && (
            <div className="space-y-6">
              {/* Connection Information */}
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Connection Information
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <GenericInput
                    label="Subscription ID"
                    value={formData.subId || ""}
                    onChange={(value) => handleInputChange("subId", value)}
                  />
                  <GenericInput
                    label="Connection Type"
                    value={formData.connectionType || ""}
                    onChange={(value) => handleInputChange("connectionType", value)}
                  />
                  <GenericInput
                    label="Capacity (Mbps)"
                    value={formData.capacityMbps ? String(formData.capacityMbps) : ""}
                    onChange={(value) => handleInputChange("capacityMbps", Number(value) || 0)}
                    type="number"
                  />
                </div>
              </GenericCard>

              {/* Source Information */}
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Source Information
                </h3>
                
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="flex space-x-4" aria-label="Tabs">
                    {SOURCE_TABS.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveSourceTab(tab.id as "datacenter" | "hardware" | "port")}
                        className={`
                          px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                          ${
                            activeSourceTab === tab.id
                              ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                              : "text-gray-500 hover:text-gray-700"
                          }
                        `}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Basic Source IDs - Removed, not needed */}

                {/* Tab Content */}
                <div className="mt-4">
                  {/* Datacenter Tab */}
                  {activeSourceTab === "datacenter" && (
                    <>
                      <div className="mb-4">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                          Source Datacenter
                        </label>
                        <GenericDropdown
                          items={sourceDataCenterOptions}
                          value={isLoadingSourceDataCenters ? "Loading..." : selectedSourceDatacenterLabel}
                          onSelect={handleSourceDatacenterSelect}
                          defaultLabel="Select Datacenter"
                          disabled={isLoadingSourceDataCenters}
                          minWidthClass="min-w-[300px]"
                          maxWidthClass="max-w-[400px]"
                          dropdownMaxHeightClass="max-h-48"
                          fixedPosition={true}
                        />
                      </div>
                      
                      {/* Always show inputs (empty or with data) */}
                      <div className="relative">
                        {isLoadingSourceDatacenterDetails && (
                          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                            <div className="flex flex-col items-center gap-2">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                              <p className="text-sm text-gray-500">Loading datacenter details...</p>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-4">
                          <GenericInput
                            label="Datacenter ID"
                            value={formData.datacenterSource?.datacenterId || selectedSourceDatacenter?.datacenterId || ""}
                            readOnly
                          />
                          <GenericInput
                            label="Display Name"
                            value={formData.datacenterSource?.displayName || selectedSourceDatacenter?.displayName || ""}
                            readOnly
                          />
                          <GenericInput
                            label="Direction"
                            value={formData.datacenterSource?.direction || selectedSourceDatacenter?.direction || ""}
                            readOnly
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Hardware Tab */}
                    {activeSourceTab === "hardware" && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Source Datacenter
                            </label>
                            <GenericDropdown
                              items={sourceDataCenterOptions}
                              value={selectedSourceDatacenter?.displayName || ""}
                              onSelect={() => {}} // Disabled
                              defaultLabel="Select Datacenter"
                              disabled={true}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Source Hardware
                            </label>
                            <GenericDropdown
                              items={sourceHardwareOptions}
                              value={selectedSourceHardwareLabel}
                              onSelect={handleSourceHardwareSelect}
                              defaultLabel="Select Hardware"
                              disabled={!selectedSourceDatacenter?.datacenterId || isLoadingSourceHardwareList}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                        </div>
                        
                        {/* Show message if no hardware available */}
                        {(selectedSourceDatacenter?.datacenterId || formData.datacenterSource?.datacenterId || formData.datacenterIdSource) && 
                         !isLoadingSourceHardwareList && 
                         sourceHardwareList.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No Hardware available</p>
                          </div>
                        )}
                        
                        {/* Always show inputs (empty or with data) */}
                        {!((selectedSourceDatacenter?.datacenterId || formData.datacenterSource?.datacenterId || formData.datacenterIdSource) && 
                            !isLoadingSourceHardwareList && 
                            sourceHardwareList.length === 0) && (
                          <div className="relative">
                            {isLoadingSourceHardwareDetails && (
                              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                                <div className="flex flex-col items-center gap-2">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                                  <p className="text-sm text-gray-500">Loading hardware details...</p>
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-4">
                            <GenericInput
                              label="Hostname"
                              value={formData.hardwareSource?.hostname || selectedSourceHardwareDetails?.hostname || ""}
                              readOnly
                            />
                          </div>
                        </div>
                        )}
                      </>
                    )}

                    {/* Port Tab */}
                    {activeSourceTab === "port" && (
                      <>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Source Datacenter
                            </label>
                            <GenericDropdown
                              items={sourceDataCenterOptions}
                              value={
                                selectedSourceDatacenter?.displayName || 
                                formData.datacenterSource?.displayName || 
                                "Select All"
                              }
                              onSelect={handleSourceDatacenterSelect}
                              defaultLabel="Select All"
                              disabled={isLoadingSourceDataCenters}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Source Hardware
                            </label>
                            <GenericDropdown
                              items={sourceHardwareOptions}
                              value={
                                selectedSourceHardwareLabel || 
                                (formData.hardwareSource?.hostname && formData.hardwareSource?.serialNumber
                                  ? `${formData.hardwareSource.hostname} - ${formData.hardwareSource.serialNumber}`
                                  : formData.hardwareSource?.hostname || "") ||
                                "Select All"
                              }
                              onSelect={handleSourceHardwareSelect}
                              defaultLabel="Select All"
                              disabled={!(selectedSourceDatacenter?.datacenterId || formData.datacenterSource?.datacenterId || formData.datacenterIdSource) || isLoadingSourceHardwareList}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Source Port
                            </label>
                            <GenericDropdown
                              items={sourcePortOptions}
                              value={selectedSourcePortLabel || formData.portSource?.portNumber || ""}
                              onSelect={handleSourcePortSelect}
                              defaultLabel="Select Port"
                              disabled={!(selectedSourceHardwareDetails?.id || formData.hardwareSource?.id || formData.hardwareIdSource) || isLoadingSourcePortsList}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                        </div>
                        
                        {/* Show message if no ports available */}
                        {(selectedSourceHardwareDetails?.id || formData.hardwareSource?.id || formData.hardwareIdSource) && 
                         !isLoadingSourcePortsList && 
                         sourcePortsList.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No Port Available</p>
                          </div>
                        )}
                        
                        {/* Always show inputs (empty or with data) */}
                        {!((selectedSourceHardwareDetails?.id || formData.hardwareSource?.id || formData.hardwareIdSource) && 
                            !isLoadingSourcePortsList && 
                            sourcePortsList.length === 0) && (
                          <div className="relative">
                            {isLoadingSourcePortDetails && (
                              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                                <div className="flex flex-col items-center gap-2">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                                  <p className="text-sm text-gray-500">Loading port details...</p>
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-4">
                            <GenericInput
                              label="Port Number"
                              value={formData.portSource?.portNumber || selectedSourcePortDetails?.portNumber || ""}
                              readOnly
                            />
                          </div>
                        </div>
                        )}
                      </>
                    )}
                </div>
              </GenericCard>

              {/* Destination Information */}
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Destination Information
                </h3>
                
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="flex space-x-4" aria-label="Tabs">
                    {DEST_TABS.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => handleDestTabChange(tab.id as "datacenter" | "hardware" | "port")}
                        className={`
                          px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                          ${
                            activeDestTab === tab.id
                              ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                              : "text-gray-500 hover:text-gray-700"
                          }
                        `}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Basic Destination IDs - Removed, not needed */}

                {/* Tab Content */}
                {(() => {
                  // For datacenter tab, show dropdown and always show inputs
                  if (activeDestTab === "datacenter") {
                    return (
                      <div className="mt-4">
                        <div className="mb-4">
                          <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                            Destination Datacenter
                          </label>
                          <GenericDropdown
                            items={dataCenterOptions}
                            value={isLoadingDataCenters ? "Loading..." : selectedDestDatacenterLabel}
                            onSelect={handleDestDatacenterSelect}
                            defaultLabel="Select All"
                            disabled={isLoadingDataCenters}
                            minWidthClass="min-w-[300px]"
                            maxWidthClass="max-w-[400px]"
                            dropdownMaxHeightClass="max-h-48"
                            fixedPosition={true}
                          />
                        </div>
                        
                        {/* Always show inputs (empty or with data) */}
                        <div className="relative">
                          {isLoadingDestDatacenterDetails && (
                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                              <div className="flex flex-col items-center gap-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                                <p className="text-sm text-gray-500">Loading datacenter details...</p>
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-4">
                            <GenericInput
                              label="Datacenter ID"
                              value={formData.datacenterDest?.datacenterId || selectedDestDatacenter?.datacenterId || ""}
                              readOnly
                            />
                            <GenericInput
                              label="Display Name"
                              value={formData.datacenterDest?.displayName || selectedDestDatacenter?.displayName || ""}
                              readOnly
                            />
                            <GenericInput
                              label="Direction"
                              value={formData.datacenterDest?.direction || selectedDestDatacenter?.direction || ""}
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // For hardware tab, show dropdowns and always show inputs
                  if (activeDestTab === "hardware") {
                    // Get datacenter ID from selectedDestDatacenter or from formData.datacenterDest
                    const datacenterId = selectedDestDatacenter?.datacenterId || formData.datacenterDest?.datacenterId;
                    const datacenterDisplayName = selectedDestDatacenter?.displayName || formData.datacenterDest?.displayName || "";
                    
                    return (
                      <div className="mt-4">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Destination Datacenter
                            </label>
                            <GenericDropdown
                              items={dataCenterOptions}
                              value={datacenterDisplayName || "Select All"}
                              onSelect={() => {console.log("log")}} // Disabled
                              defaultLabel="Select All"
                              disabled={true}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Destination Hardware
                            </label>
                            <GenericDropdown
                              items={hardwareOptionsForDest}
                              value={selectedDestHardwareLabel}
                              onSelect={handleDestHardwareSelectForTab}
                              defaultLabel="Select Hardware"
                              disabled={!datacenterId || isLoadingHardwareList}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                        </div>
                        
                        {/* Show message if no hardware available */}
                        {datacenterId && 
                         !isLoadingHardwareList && 
                         hardwareListForDest.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No Hardware available</p>
                          </div>
                        )}
                        
                        {/* Always show hardware inputs (empty or with data) */}
                        {!(datacenterId && !isLoadingHardwareList && hardwareListForDest.length === 0) && (
                          <div className="relative">
                            {isLoadingHardwareDetails && (
                              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                                <div className="flex flex-col items-center gap-2">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                                  <p className="text-sm text-gray-500">Loading hardware details...</p>
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-4">
                            <GenericInput
                              label="Hostname"
                              value={selectedDestHardwareDetails?.hostname || ""}
                              readOnly
                            />
                          </div>
                        </div>
                        )}
                      </div>
                    );
                  }

                  // For port tab, show dropdowns and always show inputs
                  if (activeDestTab === "port") {
                    // Get datacenter and hardware from selectedDestDatacenter/selectedDestHardwareDetails or from formData
                    const datacenterDisplayName = 
                      selectedDestDatacenter?.displayName || 
                      formData.datacenterDest?.displayName || 
                      "Select All";
                    const hardwareLabel = 
                      selectedDestHardwareLabel || 
                      (formData.hardwareDest?.hostname && formData.hardwareDest?.serialNumber
                        ? `${formData.hardwareDest.hostname} - ${formData.hardwareDest.serialNumber}`
                        : formData.hardwareDest?.hostname || "") ||
                      "Select All";
                    const hardwareId = selectedDestHardwareDetails?.id || formData.hardwareDest?.id || formData.hardwareIdDest;
                    const datacenterId = selectedDestDatacenter?.datacenterId || formData.datacenterDest?.datacenterId || formData.datacenterIdDest;
                    
                    return (
                      <div className="mt-4">
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Destination Datacenter
                            </label>
                            <GenericDropdown
                              items={dataCenterOptions}
                              value={datacenterDisplayName}
                              onSelect={handleDestDatacenterSelect}
                              defaultLabel="Select All"
                              disabled={isLoadingDataCenters}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Destination Hardware
                            </label>
                            <GenericDropdown
                              items={hardwareOptionsForDest}
                              value={hardwareLabel}
                              onSelect={handleDestHardwareSelectForTab}
                              defaultLabel="Select All"
                              disabled={!datacenterId || isLoadingHardwareList}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                              Destination Port
                            </label>
                            <GenericDropdown
                              items={portOptions}
                              value={selectedDestPortLabel || formData.portDest?.portNumber || ""}
                              onSelect={handleDestPortSelectForTab}
                              defaultLabel="Select Port"
                              disabled={!hardwareId || isLoadingDestPorts}
                              minWidthClass="min-w-[300px]"
                              maxWidthClass="max-w-[400px]"
                              dropdownMaxHeightClass="max-h-48"
                              fixedPosition={true}
                            />
                          </div>
                        </div>
                        
                        {/* Show message if no ports available */}
                        {hardwareId && 
                         !isLoadingDestPorts && 
                         destPorts.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No Port Available</p>
                          </div>
                        )}
                        
                        {/* Always show inputs (empty or with data) */}
                        {!(hardwareId && !isLoadingDestPorts && destPorts.length === 0) && (
                          <div className="relative">
                            {isLoadingDestPorts && (
                              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded">
                                <div className="flex flex-col items-center gap-2">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-semantic-border-brand-primary"></div>
                                  <p className="text-sm text-gray-500">Loading port details...</p>
                                </div>
                              </div>
                            )}
                            <div className="grid grid-cols-3 gap-4">
                              <GenericInput
                                label="Port Number"
                                value={formData.portDest?.portNumber || ""}
                                readOnly
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  return null;
                })()}
              </GenericCard>
            </div>
          )
        )}

        <div className="flex justify-end gap-2 mt-6">
          <GenericButton 
            label="Cancel" 
            variant="secondary" 
            onClick={onClose}
            disabled={isSaving}
          />
          <GenericButton 
            label={isSaving ? "Saving..." : "Save"} 
            variant="brand" 
            onClick={handleSave}
            disabled={isSaving || isLoading}
          />
        </div>
      </GenericModal>
    </>
  );
};
